import Image from 'next/image';
import { useRouter } from 'next/router';
import { FC, useCallback } from 'react';
import StrapiRichText from 'src/components/common/StrapiRichText';
import { ModalContainer } from 'src/components/modals/ModalContainer/ModalContainer';
import ModalMobile from 'src/components/modals/ModalMobile';
import useResponsive from 'src/hooks/contexts/useResponsive';
import useKeyPress from 'src/hooks/events/useKeyPress';
import Flex from 'src/styles/layouts/Flex';
import { EFlex } from 'src/styles/layouts/Flex/Flex.enum';
import NGButton from 'src/styles/theme/components/NGButton';
import NGHeading from 'src/styles/theme/components/NGHeading';
import { EIcon } from 'src/styles/theme/components/NGIcon/NGIcon.enum';
import { ETextType } from 'src/styles/theme/components/NGText/NGText.enum';
import { EOverflow, ESize, ETextColor, EThemeColor } from 'src/styles/theme/utils/enums';
import { StyledDesktopCloseIcon, StyledImageContainer, StyledNewsModalContainer } from './NewsModal.styles';
import { INewsModal } from './NewsModal.type';

const NewsModal: FC<INewsModal> = (props) => {
	const { isOpen, onClose } = props;

	const { isSmallerThanMd } = useResponsive();

	useKeyPress('Escape', onClose, !isOpen);
	useKeyPress('Enter', onClose, !isOpen);

	if (isSmallerThanMd) {
		return (
			<ModalMobile id='news-modal' isOpen={!!isOpen} onClose={onClose} zIndex={900} increaseCrossButtonVisibility>
				<ModalContent {...props} />
			</ModalMobile>
		);
	}

	return (
		<ModalContainer
			isOpen={!!isOpen}
			onClose={onClose}
			id='news-modal'
			size={ESize.l}
			backgroundColor={EThemeColor.black}
		>
			<StyledDesktopCloseIcon data-cy='close-modal' onClick={onClose} startIcon={EIcon.cross} size={ESize['2xs']} />
			<ModalContent {...props} />
		</ModalContainer>
	);
};

const ModalContent: FC<INewsModal> = ({
	content = [],
	imgUrl,
	title = 'Information',
	closeButtonLabel = 'Close',
	onClose,
	isExternalUrl,
	onConfirmationUrl
}) => {
	const router = useRouter();

	const onCloseWithConfirmation = useCallback(() => {
		onClose();

		if (!isExternalUrl && onConfirmationUrl) {
			router.push(onConfirmationUrl);
		}
	}, [isExternalUrl, onClose, onConfirmationUrl, router]);

	const renderConfirmationButton = useCallback(
		() => (
			<NGButton onClick={onCloseWithConfirmation} mdMarginTop={ESize.xs} marginTop={ESize.m} width='fit-content'>
				{closeButtonLabel}
			</NGButton>
		),
		[closeButtonLabel, onCloseWithConfirmation]
	);

	return (
		<StyledNewsModalContainer data-cy='news-modal'>
			<Flex mdDirection={EFlex.columnReverse}>
				<Flex
					direction={EFlex.column}
					horizontal={EFlex.center}
					vertical={EFlex.center}
					mdMaxHeight={ESize.unset}
					maxHeight='28vw'
					mdPadding={ESize['2xs']}
					paddingRight={ESize.xs}
				>
					<NGHeading width='100%' type={ETextType.h3} textColor={ETextColor.gold} marginBottom={ESize['4xs']}>
						{title}
					</NGHeading>

					<Flex
						direction={EFlex.column}
						fullWidth
						fullHeight
						mdPaddingRight={ESize.unset}
						paddingRight={ESize['5xs']}
						overflow={EOverflow.auto}
					>
						{content.map(({ id, data, type }, i) => (
							<StrapiRichText
								key={id}
								isFirst={i === 0}
								data={data}
								type={type}
								paragraphPaddingRight={ESize.unset}
								paragraphPaddingBottom={ESize.unset}
								paragraphPaddingLeft={ESize.unset}
							/>
						))}
					</Flex>
				</Flex>

				<StyledImageContainer>
					<Image priority quality={100} src={imgUrl} alt='' layout='fill' objectFit='cover' draggable={false} />
				</StyledImageContainer>
			</Flex>

			{isExternalUrl ? (
				<a href={onConfirmationUrl} target='_blank' rel='noopener noreferrer'>
					{renderConfirmationButton()}
				</a>
			) : (
				renderConfirmationButton()
			)}
		</StyledNewsModalContainer>
	);
};

export { NewsModal };
